import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { deckService } from '../services/deckService';
import { UserDecksResponse, DeckWithCreator } from '../types';
import { formatStudyTime } from '../utils/timeUtils';
import DayStatsModal from '../components/DayStatsModal';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface AdminUser {
  _id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  roles: string[];
  lastLogin: string;
  totalStudyTime: number;
  cardsStudied: number;
  createdAt: string;
  subscriptionTier: 'free' | 'premium';
  subscriptionExpiresAt?: string;
  subscriptionValue?: number;
  isSubscriptionActive: boolean;
}

interface PlatformStats {
  totalUsers: number;
  verifiedUsers: number;
  admins: number;
  activeToday: number;
  verificationRate: number;
  premiumUsers?: number;
  freeUsers?: number;
}

interface LogFile {
  name: string;
  size: number;
  modified: string;
  created: string;
}

interface ParsedLog {
  timestamp: string;
  level: string;
  context?: string;
  message: string;
  userEmail?: string;
  userId?: string;
  method?: string;
  path?: string;
  statusCode?: number;
  ip?: string;
  rawLine: string;
}

interface ParsedLogsResponse {
  logs: ParsedLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
  filters: {
    date: string;
    level?: string;
    search?: string;
    userEmail?: string;
  };
}

interface DailyStudyStat {
  date: string;
  cardsStudied: number;
  correctAnswers: number;
  totalSessions: number;
  totalDuration: number;
  uniqueUsers: number;
  accuracy: number;
  flashcards: number;
  quizzes: number;
  matches: number;
  studies: number;
}

interface StudyStatsResponse {
  dateRange: {
    from: string;
    to: string;
  };
  dailyStats: DailyStudyStat[];
  summary: {
    totalCardsStudied: number;
    totalCorrectAnswers: number;
    totalSessions: number;
    totalDuration: number;
    uniqueUsers: number;
    overallAccuracy: number;
    averageDuration: number;
    flashcards?: number;
    quizzes?: number;
    matches?: number;
    studies?: number;
  };
}

interface DayStatsData {
  date: string;
  activeUsers: {
    _id: string;
    name: string;
    email: string;
    cardsStudied?: number;
  }[];
  cardsStudied: {
    _id: string;
    front: string;
    back: string;
    deck: string;
    correct: boolean;
    timeSpent: number;
    quality: number;
    userId?: string;
    userName?: string;
    userEmail?: string;
  }[];
  sessions?: {
    _id: string;
    user: {
      _id: string;
      name: string;
      email: string;
    };
    deck: {
      _id: string;
      name: string;
    };
    cardsStudied: number;
    correctAnswers: number;
    duration: number;
    completedAt: string;
    accuracy: number;
  }[];
  flashcardSessions?: {
    _id: string;
    user: {
      _id: string;
      name: string;
      email: string;
    };
    deck: {
      _id: string;
      name: string;
    };
    cardsStudied: number;
    correctAnswers: number;
    duration: number;
    completedAt: string;
    accuracy: number;
  }[];
  quizSessions?: {
    _id: string;
    user: {
      _id: string;
      name: string;
      email: string;
    };
    score?: number;
    totalQuestions?: number;
    correctAnswers?: number;
    timeSpent?: number;
    startedAt?: string;
    completedAt?: string;
  }[];
  matchSessions?: {
    _id: string;
    user: {
      _id: string;
      name: string;
      email: string;
    };
    timeSpent?: number;
    movesCount?: number;
    startedAt?: string;
    completedAt?: string;
  }[];
  totalSessions: number;
  totalStudyTime: number;
  totalCardsStudied: number;
  totalCorrectAnswers: number;
  flashcardsCount?: number;
  quizzesCount?: number;
  matchesCount?: number;
  studiesCount?: number;
}

const Admin: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'logs' | 'statistics'>('users');
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [studyStats, setStudyStats] = useState<StudyStatsResponse | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsDateRange, setStatsDateRange] = useState({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
    to: new Date().toISOString().split('T')[0] // today
  });
  
  // Parsed logs states
  const [parsedLogs, setParsedLogs] = useState<ParsedLogsResponse | null>(null);
  const [loadingParsedLogs, setLoadingParsedLogs] = useState(false);
  const [logsTab, setLogsTab] = useState<'file-logs' | 'parsed-logs'>('parsed-logs');
  const [parsedLogsFilters, setParsedLogsFilters] = useState({
    page: 1,
    limit: 50,
    level: 'INFO', // Default to INFO logs
    search: '',
    userEmail: '',
    date: new Date().toISOString().split('T')[0], // today
  });
  
  // Day stats modal states
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState<DayStatsData | null>(null);
  const [loadingDayStats, setLoadingDayStats] = useState(false);
  
  // User filter states
  const [userFilters, setUserFilters] = useState({
    name: '',
    email: '',
    createdFrom: '',
    createdTo: '',
    subscriptionTier: '',
    page: 1,
    limit: 10
  });
  
  // Subscription editing states
  const [editingSubscription, setEditingSubscription] = useState<string | null>(null);
  const [newExpirationDate, setNewExpirationDate] = useState<string>('');
  const [editingSubscriptionValue, setEditingSubscriptionValue] = useState<string | null>(null);
  const [newSubscriptionValue, setNewSubscriptionValue] = useState<string>('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && activeTab === 'logs' && logsTab === 'parsed-logs') {
      fetchParsedLogs();
    }
  }, [isAdmin, activeTab, logsTab]);

  const fetchData = async (filters = userFilters) => {
    try {
      // Build query parameters for user filtering
      const queryParams = new URLSearchParams();
      if (filters.name) queryParams.append('name', filters.name);
      if (filters.email) queryParams.append('email', filters.email);
      if (filters.createdFrom) queryParams.append('createdFrom', filters.createdFrom);
      if (filters.createdTo) queryParams.append('createdTo', filters.createdTo);
      if (filters.subscriptionTier) queryParams.append('subscriptionTier', filters.subscriptionTier);
      queryParams.append('page', filters.page.toString());
      queryParams.append('limit', filters.limit.toString());

      const [usersResponse, statsResponse] = await Promise.all([
        api.get(`/admin/users?${queryParams.toString()}`),
        api.get('/admin/stats')
      ]);
      
      setUsers(usersResponse.data.users);
      setPagination(usersResponse.data.pagination);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { ...userFilters, [field]: value, page: 1 };
    setUserFilters(newFilters);
  };

  const applyFilters = () => {
    setLoading(true);
    fetchData(userFilters);
  };

  const clearFilters = () => {
    const defaultFilters = {
      name: '',
      email: '',
      createdFrom: '',
      createdTo: '',
      subscriptionTier: '',
      page: 1,
      limit: 10
    };
    setUserFilters(defaultFilters);
    setLoading(true);
    fetchData(defaultFilters);
  };

  const handlePageChange = (newPage: number) => {
    const newFilters = { ...userFilters, page: newPage };
    setUserFilters(newFilters);
    setLoading(true);
    fetchData(newFilters);
  };

  const updateUserSubscription = async (userId: string, tier: 'free' | 'premium', expiresAt?: string, subscriptionValue?: number) => {
    try {
      const payload: any = { tier };
      if (expiresAt && tier === 'premium') {
        payload.subscriptionExpiresAt = expiresAt;
      }
      if (subscriptionValue !== undefined) {
        payload.subscriptionValue = subscriptionValue;
      }
      
      await api.put(`/admin/users/${userId}/subscription`, payload);
      await fetchData(); // Refresh data
    } catch (error) {
      console.error('Failed to update user subscription:', error);
    }
  };

  const fetchStudyStats = async (dateFrom?: string, dateTo?: string) => {
    setLoadingStats(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      
      const response = await api.get(`/admin/study-stats?${params.toString()}`);
      setStudyStats(response.data);
    } catch (error) {
      console.error('Failed to fetch study statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchDayDetailedStats = async (date: string) => {
    setLoadingDayStats(true);
    try {
      const response = await api.get(`/admin/study-stats/day/${date}`);
      setSelectedDayData(response.data);
      setIsDayModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch detailed day statistics:', error);
      setSelectedDayData(null);
    } finally {
      setLoadingDayStats(false);
    }
  };

  const fetchLogFiles = async () => {
    setLoadingLogs(true);
    try {
      const response = await api.get('/admin/logs');
      setLogFiles(response.data.files);
    } catch (error) {
      console.error('Failed to fetch log files:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchParsedLogs = async (filters = parsedLogsFilters) => {
    setLoadingParsedLogs(true);
    try {
      const params = new URLSearchParams();
      params.append('page', filters.page.toString());
      params.append('limit', filters.limit.toString());
      if (filters.level) params.append('level', filters.level);
      if (filters.search) params.append('search', filters.search);
      if (filters.userEmail) params.append('userEmail', filters.userEmail);
      if (filters.date) params.append('date', filters.date);

      const response = await api.get(`/admin/logs/parsed?${params.toString()}`);
      setParsedLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch parsed logs:', error);
      setParsedLogs(null);
    } finally {
      setLoadingParsedLogs(false);
    }
  };

  const handleParsedLogsFilterChange = (field: string, value: string | number) => {
    const newFilters = { ...parsedLogsFilters, [field]: value, page: 1 };
    setParsedLogsFilters(newFilters);
  };

  const applyParsedLogsFilters = () => {
    fetchParsedLogs(parsedLogsFilters);
  };

  const clearParsedLogsFilters = () => {
    const defaultFilters = {
      page: 1,
      limit: 50,
      level: 'INFO', // Default to INFO logs
      search: '',
      userEmail: '',
      date: new Date().toISOString().split('T')[0],
    };
    setParsedLogsFilters(defaultFilters);
    fetchParsedLogs(defaultFilters);
  };

  const downloadLogFile = async (filename: string) => {
    try {
      const response = await api.get(`/admin/logs/${filename}?download=true`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download log file:', error);
      alert('Failed to download log file. Please try again.');
    }
  };

  // Extract unique dates from log files
  const getAvailableDates = () => {
    const dates = new Set<string>();
    logFiles.forEach(file => {
      // Extract date from filename (e.g., application-2025-09-08.log -> 2025-09-08)
      const match = file.name.match(/(\d{4}-\d{2}-\d{2})/);
      if (match) {
        dates.add(match[1]);
      }
    });
    return Array.from(dates).sort().reverse(); // Most recent first
  };

  // Filter log files by selected date
  const getFilteredLogFiles = () => {
    if (!selectedDate) return logFiles;
    return logFiles.filter(file => file.name.includes(selectedDate));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isAdmin) {
    return (
      <div className="text-center">
        <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">Access Denied</h1>
        <p className="text-gray-600 dark:text-gray-400">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin</h1>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">Manage users and view platform statistics</p>
      </div>

      {/* Platform Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalUsers}</div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Verified Users</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.verifiedUsers}</div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Premium Users</div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.premiumUsers || 0}</div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Free Users</div>
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">{stats.freeUsers || 0}</div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Admins</div>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.admins}</div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Today</div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.activeToday}</div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Verification Rate</div>
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.verificationRate.toFixed(1)}%</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Users Management
          </button>
          <button
            onClick={() => navigate('/admin/content-management')}
            className={`py-2 px-1 border-b-2 font-medium text-sm border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600`}
          >
            Content Management
          </button>
          <button
            onClick={() => {
              setActiveTab('logs');
              if (logFiles.length === 0) {
                fetchLogFiles();
              }
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'logs'
                ? 'border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Logs
          </button>
          <button
            onClick={() => {
              setActiveTab('statistics');
              if (!studyStats) {
                fetchStudyStats(statsDateRange.from, statsDateRange.to);
              }
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'statistics'
                ? 'border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            Statistics
          </button>
        </nav>
      </div>

      {/* Users Table */}
      {activeTab === 'users' && (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Users ({pagination.totalCount || users.length})
            </h3>
          </div>

          {/* Filter Form */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Search & Filter Users</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label htmlFor="name-filter" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name-filter"
                  placeholder="Search by name..."
                  value={userFilters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="email-filter" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email-filter"
                  placeholder="Search by email..."
                  value={userFilters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="created-from-filter" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Created From
                </label>
                <input
                  type="date"
                  id="created-from-filter"
                  value={userFilters.createdFrom}
                  onChange={(e) => handleFilterChange('createdFrom', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="created-to-filter" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Created To
                </label>
                <input
                  type="date"
                  id="created-to-filter"
                  value={userFilters.createdTo}
                  onChange={(e) => handleFilterChange('createdTo', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="subscription-filter" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subscription
                </label>
                <select
                  id="subscription-filter"
                  value={userFilters.subscriptionTier}
                  onChange={(e) => handleFilterChange('subscriptionTier', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                >
                  <option value="">All</option>
                  <option value="free">Free</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-500 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.emailVerified 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                      }`}>
                        {user.emailVerified ? 'Verified' : 'Unverified'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map((role) => (
                          <span
                            key={role}
                            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                              role === 'admin' 
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                                : role === 'moderator'
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                            }`}
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.subscriptionTier === 'premium' 
                          ? 'bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-700' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}>
                        {user.subscriptionTier === 'premium' ? '👑 Premium' : 'Free'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(user.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => navigate(`/admin/users/${user._id}/edit`)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrev}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                    pagination.hasPrev
                      ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                      : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNext}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
                    pagination.hasNext
                      ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                      : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{' '}
                    <span className="font-medium">{Math.min((pagination.page - 1) * pagination.limit + 1, pagination.totalCount)}</span>
                    {' '}to{' '}
                    <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.totalCount)}</span>
                    {' '}of{' '}
                    <span className="font-medium">{pagination.totalCount}</span>
                    {' '}results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.hasPrev}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                        pagination.hasPrev
                          ? 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                          : 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === pagination.page
                              ? 'z-10 bg-indigo-50 dark:bg-indigo-900/50 border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-300'
                              : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.hasNext}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                        pagination.hasNext
                          ? 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                          : 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Logs Management */}
      {activeTab === 'logs' && (
        <div className="space-y-6">
          {/* Sub-tabs for Activity Logs and Raw Logs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => {
                  setLogsTab('parsed-logs');
                  if (!parsedLogs) {
                    fetchParsedLogs();
                  }
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  logsTab === 'parsed-logs'
                    ? 'border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Activity Logs
              </button>
              <button
                onClick={() => {
                  setLogsTab('file-logs');
                  if (logFiles.length === 0) {
                    fetchLogFiles();
                  }
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  logsTab === 'file-logs'
                    ? 'border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Raw Logs
              </button>
            </nav>
          </div>
          {/* Parsed Logs Content */}
          {logsTab === 'parsed-logs' && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-6">
                  Activity Logs {parsedLogs && `(${parsedLogs.pagination.total})`}
                </h3>

                {/* Filters */}
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Search & Filter Logs</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label htmlFor="log-date" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        id="log-date"
                        value={parsedLogsFilters.date}
                        onChange={(e) => handleParsedLogsFilterChange('date', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="log-level" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Log Level
                      </label>
                      <select
                        id="log-level"
                        value={parsedLogsFilters.level}
                        onChange={(e) => handleParsedLogsFilterChange('level', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                      >
                        <option value="">All Levels</option>
                        <option value="INFO">INFO</option>
                        <option value="WARN">WARN</option>
                        <option value="ERROR">ERROR</option>
                        <option value="DEBUG">DEBUG</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="log-user-email" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        User Email
                      </label>
                      <input
                        type="text"
                        id="log-user-email"
                        placeholder="Search by email..."
                        value={parsedLogsFilters.userEmail}
                        onChange={(e) => handleParsedLogsFilterChange('userEmail', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                    <div>
                      <label htmlFor="log-search" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Search Message
                      </label>
                      <input
                        type="text"
                        id="log-search"
                        placeholder="Search in logs..."
                        value={parsedLogsFilters.search}
                        onChange={(e) => handleParsedLogsFilterChange('search', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      onClick={clearParsedLogsFilters}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Clear Filters
                    </button>
                    <button
                      onClick={applyParsedLogsFilters}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-500 rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>

                {/* Loading State */}
                {loadingParsedLogs ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                  </div>
                ) : !parsedLogs || parsedLogs.logs.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No logs found for the selected filters.
                  </p>
                ) : (
                  <>
                    {/* Logs Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Time
                            </th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Level
                            </th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              User Email
                            </th>
                            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Message
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {parsedLogs.logs.map((log, index) => (
                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                                {log.timestamp.split(' ')[1]}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  log.level === 'ERROR' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                  log.level === 'WARN' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                  log.level === 'INFO' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                                  'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                }`}>
                                  {log.level}
                                </span>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm">
                                {log.userEmail ? (
                                  <span className="font-medium text-indigo-600 dark:text-indigo-400">
                                    {log.userEmail}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-600">-</span>
                                )}
                              </td>
                              <td className="px-3 py-4 text-sm text-gray-900 dark:text-white">
                                {log.context && (
                                  <span className="text-gray-700 dark:text-gray-300 font-medium">
                                    [{log.context}]
                                  </span>
                                )}
                                {log.message.includes('AUDIT FAIL:') ? (
                                  <div className="space-y-1">
                                    <div className="font-medium text-red-600 dark:text-red-400">AUDIT FAIL:</div>
                                    <pre className="text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-x-auto whitespace-pre-wrap break-words">
                                      {(() => {
                                        try {
                                          const jsonMatch = log.message.match(/\{.*\}/);
                                          if (jsonMatch) {
                                            const jsonData = JSON.parse(jsonMatch[0]);
                                            return JSON.stringify(jsonData, null, 2);
                                          }
                                          return log.message;
                                        } catch {
                                          return log.message;
                                        }
                                      })()}
                                    </pre>
                                  </div>
                                ) : (
                                  log.message
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        Showing page {parsedLogs.pagination.page} of {parsedLogs.pagination.totalPages} 
                        ({parsedLogs.pagination.total} total logs)
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setParsedLogsFilters({ ...parsedLogsFilters, page: parsedLogsFilters.page - 1 });
                            fetchParsedLogs({ ...parsedLogsFilters, page: parsedLogsFilters.page - 1 });
                          }}
                          disabled={parsedLogsFilters.page === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => {
                            setParsedLogsFilters({ ...parsedLogsFilters, page: parsedLogsFilters.page + 1 });
                            fetchParsedLogs({ ...parsedLogsFilters, page: parsedLogsFilters.page + 1 });
                          }}
                          disabled={!parsedLogs.pagination.hasMore}
                          className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* File Logs Content */}
          {logsTab === 'file-logs' && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4 sm:mb-0">
                    Log Files ({getFilteredLogFiles().length})
                  </h3>
                  
                  {/* Date Filter */}
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Filter by date:
                    </label>
                    <select
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
                    >
                      <option value="">All dates</option>
                      {getAvailableDates().map(date => (
                        <option key={date} value={date}>{date}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {loadingLogs ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                  </div>
                ) : getFilteredLogFiles().length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                    {selectedDate ? `No log files found for ${selectedDate}.` : 'No log files found.'}
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {getFilteredLogFiles().map((file) => (
                      <div 
                        key={file.name} 
                        className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg p-4 transition-colors"
                      >
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">{file.name}</h4>
                        <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1 mb-3">
                          <div>Size: {formatFileSize(file.size)}</div>
                          <div>Modified: {new Date(file.modified).toLocaleString()}</div>
                        </div>
                        <button
                          onClick={() => downloadLogFile(file.name)}
                          className="w-full px-3 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-500 border border-transparent rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors"
                        >
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statistics Dashboard */}
      {activeTab === 'statistics' && (
        <div className="space-y-6">
          {/* Date Range Filter */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4 sm:mb-0">
                  Study Statistics
                </h3>
                
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      From:
                    </label>
                    <input
                      type="date"
                      value={statsDateRange.from}
                      onChange={(e) => setStatsDateRange(prev => ({ ...prev, from: e.target.value }))}
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      To:
                    </label>
                    <input
                      type="date"
                      value={statsDateRange.to}
                      onChange={(e) => setStatsDateRange(prev => ({ ...prev, to: e.target.value }))}
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => fetchStudyStats(statsDateRange.from, statsDateRange.to)}
                    disabled={loadingStats}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-500 border border-transparent rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loadingStats ? 'Loading...' : 'Apply Filter'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Cards Studied Per Day Chart */}
          {studyStats?.dailyStats && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Activity Per Day
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Click on any bar to see detailed information
                  </p>
                </div>
                
                {studyStats.dailyStats.length > 0 ? (
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={studyStats.dailyStats}
                        onClick={(data) => {
                          if (data && data.activeLabel) {
                            fetchDayDetailedStats(data.activeLabel);
                          }
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#6B7280"
                          tick={{ fontSize: 12 }}
                          tickFormatter={(date) => {
                            // Parse the date string (YYYY-MM-DD) correctly to avoid timezone issues
                            const [year, month, day] = date.split('-').map(Number);
                            const d = new Date(year, month - 1, day); // month is 0-indexed in JavaScript Date
                            return `${d.getMonth() + 1}/${d.getDate()}`;
                          }}
                        />
                        <YAxis stroke="#6B7280" tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#1F2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: '#F9FAFB'
                          }}
                          labelFormatter={(date) => {
                            // Parse the date string (YYYY-MM-DD) correctly to avoid timezone issues
                            const [year, month, day] = date.split('-').map(Number);
                            const d = new Date(year, month - 1, day); // month is 0-indexed in JavaScript Date
                            return d.toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            });
                          }}
                          formatter={(value, name) => [
                            value.toLocaleString(),
                            name === 'totalSessions' ? 'Study Sessions' :
                            name === 'uniqueUsers' ? 'Active Users' : name
                          ]}
                        />
                        <Legend />
                        <Bar 
                          dataKey="totalSessions" 
                          fill="#F59E0B"
                          name="Study Sessions"
                          radius={[4, 4, 0, 0]}
                          cursor="pointer"
                        />
                        <Bar 
                          dataKey="uniqueUsers" 
                          fill="#10B981"
                          name="Active Users"
                          radius={[4, 4, 0, 0]}
                          cursor="pointer"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">No study data found for the selected period</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Loading State */}
          {loadingStats && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                  <span className="ml-2 text-gray-700 dark:text-gray-300">Loading statistics...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Day Stats Modal */}
      <DayStatsModal
        isOpen={isDayModalOpen}
        onClose={() => {
          setIsDayModalOpen(false);
          setSelectedDayData(null);
        }}
        data={selectedDayData}
        loading={loadingDayStats}
      />
    </div>
  );
};

export default Admin;
